import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ScrollText } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Blocking clickwrap gate: every authenticated user must have accepted the
// CURRENT Terms of Service + Privacy Policy versions to use the app. Shows
// once, and again automatically whenever either version is bumped on the
// backend (routes/legal.py). Registration logs its own acceptance, so brand
// new signups never see this.
export const LegalGate = () => {
  const { user } = useAuth();
  const [state, setState] = useState(null); // null = checking
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    api.get("/legal/acceptance")
      .then(({ data }) => {
        if (!alive) return;
        const upToDate = data.accepted
          && data.terms_version === data.current_terms_version
          && data.privacy_version === data.current_privacy_version;
        setState(upToDate ? { ok: true } : { ok: false, versions: data });
      })
      .catch(() => alive && setState({ ok: true })); // never lock users out on a network blip
    return () => { alive = false; };
  }, [user]);

  if (!user || !state || state.ok) return null;

  const isUpdate = Boolean(state.versions?.accepted);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/legal/acceptance", {
        terms_version: state.versions.current_terms_version,
        privacy_version: state.versions.current_privacy_version,
        method: isUpdate ? "reacceptance_clickwrap" : "app_clickwrap",
      });
      setState({ ok: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="ld-card p-6 max-w-lg w-full space-y-4 border border-[var(--ld-royal)]/30"
           data-testid="legal-gate-modal">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[var(--ld-royal)]/20 text-[var(--ld-royal-hover)] flex items-center justify-center shrink-0">
            <ScrollText size={18} />
          </div>
          <div>
            <div className="font-heading font-semibold text-lg text-white">
              {isUpdate ? "We've updated our terms" : "One quick thing before you continue"}
            </div>
            <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">
              {isUpdate
                ? "Our Terms of Service and/or Privacy Policy have changed. Please review and accept the current versions to keep using Legacy Detailing IO."
                : "To use Legacy Detailing IO, please review and accept our Terms of Service and Privacy Policy."}
            </p>
            <p className="text-sm text-slate-400 mt-3 leading-relaxed">
              Read the{" "}
              <Link to="/terms" target="_blank" className="text-[var(--ld-royal-hover)] hover:underline" data-testid="legal-gate-terms-link">
                Terms of Service
              </Link>{" "}
              and the{" "}
              <Link to="/privacy" target="_blank" className="text-[var(--ld-royal-hover)] hover:underline" data-testid="legal-gate-privacy-link">
                Privacy Policy
              </Link>{" "}
              (they open in a new tab).
            </p>
          </div>
        </div>
        <label className="flex items-start gap-2.5 text-sm text-slate-300 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={agree}
            onChange={e => setAgree(e.target.checked)}
            className="mt-0.5 accent-[var(--ld-royal)]"
            data-testid="legal-gate-checkbox"
          />
          <span>I have read and agree to the Terms of Service and acknowledge the Privacy Policy.</span>
        </label>
        <div className="flex justify-end pt-1">
          <button onClick={accept} disabled={!agree || busy}
                  className="ld-btn ld-btn-primary text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="legal-gate-accept">
            {busy ? "Saving…" : "Accept & continue"}
          </button>
        </div>
      </div>
    </div>
  );
};
